<template>
    <div ref="num-pad-div"
         class="num-pad">
        <div v-if="showValueAsCurrency"
             class="num-pad__value"
             :class="{'text-center': centerValue}">
            {{ currencyFilter(displayValue) }}
        </div>
        <div v-else-if="showValue"
             class="num-pad__value"
             :class="{'text-center': centerValue}">
            {{ maskValue ? maskFilter(displayValue) : displayValue }}
        </div>

        <div class="num-pad__cols">
            <div class="num-pad__btns">
                <a v-if="numPad.options.showClearButton"
                   class="btn dull btn-default btn-lg"
                   :class="{
                       'clear-btn': !numPad.options.showSignButton && !$slots['btn']
                   }"
                   @click="clear()">{{ l('NUM_PAD.CLEAR') }}</a>
                <a v-if="numPad.options.showSignButton"
                   class="btn dull btn-lg"
                   :class="{
                       'btn-primary': numPad.options.isNegative,
                       'btn-default': !numPad.options.isNegative
                   }"
                   @click="numPad.toggleSign()">+ / &ndash;</a>
                <slot name="btn"></slot>
                <a v-if="numPad.options.showBackspaceButton"
                   class="btn dull btn-default btn-lg"
                   @click="numPad.backspace()"><i class="fa-solid fa-delete-left" /></a>

                <div class="num-btns">
                    <a v-for="num in [7,8,9,4,5,6,1,2,3]"
                       :key="num"
                       class="btn btn-default btn-lg"
                       @click.prevent="numPad.inputValue(num)">{{ num }}</a>
                </div>

                <a class="btn btn-default btn-lg zero-btn"
                   @click="numPad.inputValue(0)">0</a>

                <a v-if="numPad.options.showDecimalButton && numPad.options.fixedDP"
                   class="btn btn-lg"
                   :class="{
                       'btn-primary': numPad.decimalHasBeenPressed,
                       'btn-default': !numPad.decimalHasBeenPressed
                   }"
                   :disabled="numPad.decimalHasBeenPressed"
                   @click="numPad.pennies()">
                    {{ numPad.getDecimalButtonText() }}
                </a>
                <a v-else-if="numPad.options.showDecimalButton && !numPad.options.fixedDP"
                   class="btn btn-lg"
                   :class="{
                       'btn-primary': numPad.decimalHasBeenPressed,
                       'btn-default': !numPad.decimalHasBeenPressed
                   }"
                   @click="numPad.decimal()">
                    {{ numPad.getDecimalButtonText() }}
                </a>
            </div>

            <div v-if="numPad.options.exactAmount !== null || numPad.options.showCashButtons"
                 class="col num-pad__right-col">
                <a v-if="numPad.options.exactAmount !== null"
                   class="btn dull btn-default btn-lg add-value-btn"
                   @click="numPad.setValue(numPad.options.exactAmount)">
                    {{ l('NUM_PAD.EXACT') }}
                </a>
                <!-- Blank space if no exact button -->
                <span v-if="numPad.options.showCashButtons && numPad.options.exactAmount == null"></span>

                <div class="num-pad__cash-btns"
                     :class="{
                         'num-pad__cash-btns--many': numPad.activeDenominationCount > 4
                     }">
                    <template v-if="numPad.options.showCashButtons">
                        <a v-for="denomination in numPad.activeDenominations"
                           :key="denomination.value"
                           class="btn btn-info btn-lg add-value-btn"
                           @click="numPad.addValue(denomination.value)">
                            {{ denomination.name }}
                        </a>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { container } from '@/container';

/**
 * This NumPad component renders a number pad on the screen for easily entering numbers, PINs, etc.
 */
export default {
    props: {
        /**
         * Allows using v-model="something" for two-way binding when using this component.
         * Any changes made in the numpad will be reflected back into this value.
         * Note: This is only partially implemented currently! If you change this value the change will not be
         * reflected in the numpad yet. So you should still call the clear() method to clear the numpad's value or
         * call setValue() to change the value externally later.
         */
        modelValue: {
            type: [String, Number],
            default: null,
        },

        /**
         * The number of decimal places to render any value.
         *
         * @type {number}
         */
        numDecimalPlaces: {
            type: Number,
            default: 2,
        },

        /**
         * If set will show an 'Exact' button which will set the amount to this value when pressed.
         *
         * @type {number|null}
         */
        exactAmount: {
            type: Number,
            default: null,
        },

        showBackspaceButton: {
            type: Boolean,
            default: true,
        },

        /**
         * Show buttons to add common denominations (20/10/5) to the value.
         *
         * @type {boolean}
         */
        showCashButtons: {
            type: Boolean,
            default: false,
        },

        showClearButton: {
            type: Boolean,
            default: true,
        },

        /**
         * Show the decimal button?
         *
         * @type {boolean}
         */
        showDecimalButton: {
            type: Boolean,
            default: true,
        },

        /**
         * Show or hide the sign toggle button.
         *
         * @type {boolean}
         */
        showSignButton: {
            type: Boolean,
            default: true,
        },

        /**
         * Keep the value as negative even when clearing?
         */
        forceNegative: {
            type: Boolean,
            default: false,
        },

        /**
         * Converts zero values to null.
         * If true, and no initial value is specified, the amount will be null until something is entered.
         * Otherwise it will be 0.
         * If true, the amount will be set to null if the clear button is pressed or the value becomes zero.
         *
         * @type {boolean}
         */
        allowNull: {
            type: Boolean,
            default: false,
        },

        /**
         * An array of fields in which not to capture the keystrokes if the field is the event target. At the
         * moment this is simple html input types e.g. textarea but could be extended to use css selectors
         * if necessary.
         *
         * @type {string[]}
         */
        doNotCaptureKeysIn: {
            type: Array,
            default: () => [
                'input',
                'select',
                'textarea',
            ],
        },

        // Display options....

        showValue: {
            type: Boolean,
            default: true,
        },

        showValueAsCurrency: {
            type: Boolean,
            default: false,
        },

        maskValue: {
            type: Boolean,
            default: false,
        },

        centerValue: {
            type: Boolean,
            default: true,
        },

        isPinMode: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'ok',
        'cancel',
        'clear',
        'update:modelValue',
    ],

    data() {
        return {
            /**
             * @type {NumPad}
             */
            numPad: null,

            displayValue: null,

            // Remember the function added as an event listener so that it can be
            // removed when the component is destroyed.
            keydownEventListener: null,
        };
    },

    created() {
        const numPadOptions = {
            allowNull: this.allowNull,
            doNotCaptureKeysIn: this.doNotCaptureKeysIn,
            exactAmount: this.exactAmount,
            fixedDP: false,
            forceNegative: this.forceNegative,
            isNegative: this.forceNegative,
            numDecimalPlaces: this.numDecimalPlaces,
            showBackspaceButton: this.showBackspaceButton,
            showCashButtons: this.showCashButtons,
            showClearButton: this.showClearButton,
            showDecimalButton: this.showDecimalButton,
            showSignButton: this.showSignButton,
            isPinMode: this.isPinMode,
            ok: () => this.$emit('ok'),
            cancel: () => this.$emit('cancel'),
        };

        this.numPad = container.numPadFactory.create(numPadOptions);
        this.numPad.onChange((value) => {
            this.displayValue = value;
            this.$emit('update:modelValue', value);
        });

        /**
         * Determine if key events should be forwarded to this num pad.
         *
         * @returns {boolean}
         */
        const listenToKeydown = () => {
            const el = this.$refs['num-pad-div'];
            const modalsAreOpen = document.querySelectorAll('.modl,.modal').length > 0;
            if (!modalsAreOpen) {
                // If no modals are open this must be directly on the page and not obscured by a modal.
                return true;
            }

            const parentVueModal = el.closest('.modl-container');
            if (!parentVueModal) {
                // Modals are open but the num pad is in inside any model.
                // It must be obscured.
                return false;
            }

            // Exception: Some modals are always on top of others (e.g. switch-user-modal).
            // If this NumPad is inside one of those, always listen to the input.
            if (parentVueModal.classList.contains('modl-container--blur')) {
                return true;
            }

            // If this modal is the topmost open modal (only considering Vue modals because Angular ones will not
            // be around much longer), accept the input.
            const openVueModals = document.querySelectorAll('.modl-container');
            const index = Array.prototype.indexOf.call(openVueModals, parentVueModal);
            return index === (openVueModals.length - 1);
        };

        // Listen for keydown events on the window, because
        // nothing in the numpad element is an input element
        // that can have focus.
        this.keydownEventListener = (event) => {
            if (listenToKeydown()) {
                this.numPad.keyDownReceived(event);
            }
        };
        document.addEventListener('keydown', this.keydownEventListener);

        if (this.modelValue) {
            this.setValue(this.modelValue);
        }
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.keydownEventListener);
    },

    methods: {
        getValue() {
            return this.numPad.getValue();
        },

        setValue(value) {
            this.numPad.setValue(value);
        },

        clear() {
            this.numPad.clear();
            this.$emit('clear');
        },
    },
};
</script>

<style lang="less" scoped>
@import (reference) "../../../less/variables";

.num-pad {
    .num-pad__value {
        margin: 0 0 @half-gutter-width;
    }

    .num-pad__cols {
        margin: 0;
        padding: 0;
    }
}
</style>
